import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import LiturgicalSeasonStrip from "../components/LiturgicalSeasonStrip";
import "./Dashboard.css";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULES = [
  { key: "profile", name: "Parish Profile", desc: "Your parish's worship, clergy, music, and customs.", to: "/parish-profile", status: "live" },
  { key: "sunday", name: "SundayBuilder™", desc: "Worship dossiers grounded in your parish profile.", to: "/worship-planner", status: "live" },
  { key: "annual", name: "Annual Planner™", desc: "Prepare an entire year of worship from one planning workspace.", to: "/annual-planner", status: "live" },
  { key: "parish", name: "Parish Profile Intelligence™", desc: "Strategic parish assessment and executive dossier.", to: "/parish-intelligence", status: "live" },
  { key: "memory", name: "Parish Memory™", desc: "Your parish's living institutional knowledge.", to: "/parish-memory", status: "live" },
  { key: "reports", name: "Report Center", desc: "Open, export, duplicate, and share every dossier.", to: "/reports", status: "live" },
  { key: "founding", name: "Founding Circle", desc: "Support the mission as a founding member.", to: "/founding-circle", status: "live" },
  { key: "music", name: "Musical Intelligence™", desc: "Suggested repertoire engine, currently in validation.", to: "/musical-intelligence", status: "validation" },
  { key: "formation", name: "Formation Planner™", desc: "Formation, catechesis, and discipleship paths.", status: "soon" },
  { key: "analytics", name: "Analytics™", desc: "Cross-parish insights and diocesan reporting.", status: "soon" },
  { key: "settings", name: "Account & Billing", desc: "Profile, plan, subscription, and billing.", to: "/account", status: "live" },
];

const NAV = [
  { key: "dashboard", label: "Dashboard" },
  ...MODULES,
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [today, setToday] = useState(null);
  const [isOwner, setIsOwner] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dashboard/today`);
      setToday(r.data);
    } catch (e) { console.warn("Dashboard today load failed:", e); }
    try { const w = await axios.get(`${API}/admin/whoami`); setIsOwner(!!w.data.is_owner); }
    catch (e) { console.warn("whoami check failed:", e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const go = (m) => {
    if ((m.status === "live" || m.status === "validation") && m.to) navigate(m.to);
  };

  return (
    <div className="dash">
      <aside className="dash-side">
        <div className="dash-logo">
          <img src="/brand/rh-monogram.png" alt="Rasaan Hakiem" className="dash-crest" />
          <div className="l1">Liturgical <span>Intelligence™</span></div>
        </div>
        <nav className="dash-nav">
          {NAV.map((n) => (
            <button
              key={n.key}
              className={`dash-navitem${n.key === "dashboard" ? " active" : ""}`}
              onClick={() => (n.key === "dashboard" ? null : go(n))}
              data-testid={`nav-${n.key}`}
            >
              <span>{n.label || n.name}</span>
              {n.status === "soon" && <span className="soon">Soon</span>}
            </button>
          ))}
        </nav>
        <div className="dash-foot">
          <p className="b">Rasaan Hakiem LLC · Ordo AI™</p>
        </div>
      </aside>

      <main className="dash-main">
        <div className="dash-bar">
          <div className="t">Dashboard</div>
          <div className="dash-user" data-testid="dash-user">
            {user && (
              <div className="dash-account">
                {user.picture
                  ? <img src={user.picture} alt="" className="dash-avatar" />
                  : <span className="dash-avatar dash-avatar-i">{(user.name || user.email || "?").charAt(0).toUpperCase()}</span>}
                <span className="dash-uname" data-testid="dash-username">{user.name || user.email}</span>
                {isOwner && <button className="dash-logout" onClick={() => navigate("/admin/billing")} data-testid="nav-admin">Admin</button>}
                {isOwner && <button className="dash-logout" onClick={() => navigate("/admin/founding-circle")} data-testid="nav-admin-founding">Founding</button>}
                <button className="dash-logout" onClick={doLogout} data-testid="logout-btn">Sign out</button>
              </div>
            )}
          </div>
        </div>

        {/* Altar-frontal sanctuary header — the primary visual anchor of
            the application. The frontal photograph is the master visual
            reference; it appears here as the identity band above every
            planning session. */}
        <div className="li-altar-header" data-testid="dash-altar-header" role="img" aria-label="Bas-relief altar frontal · master visual reference">
          <div className="li-altar-header__body">
            <h1 className="li-altar-header__title" data-testid="dash-archive-title">
              {user?.name ? `Welcome, ${user.name.split(" ")[0]}.` : "Welcome."}
            </h1>
            <p className="li-altar-header__sub">
              Your parish's planning archive.
            </p>
          </div>
        </div>

        {/* Liturgical season strip · the archival ribbon a parish
            planning archive always opens with (current season · colour ·
            next principal feast). Fetches from /api/liturgical/moment. */}
        <LiturgicalSeasonStrip />

        <div className="dash-body">

          {user?.entitlements && (() => {
            const ent = user.entitlements;
            const isFree = ent.plan === "free";
            const ob = ent.onboarding || {};
            const credits = ent.single_report_credits || 0;
            const onboardingDone = !!ob.sundaybuilder_used;
            const planName = isFree ? "Complimentary Account"
              : ({ parish_pro: "Parish Pro", cathedral: "Cathedral Edition", diocese: "Diocese Edition" }[ent.plan] || "Free");
            const meta = !isFree
              ? "Unlimited worship planning & Parish Intelligence"
              : (!onboardingDone
                  ? "Complimentary onboarding available — finish setup to generate your free plan"
                  : (credits > 0 ? `${credits} SundayBuilder™ credit(s) available`
                                 : "Complimentary plan used · 0 reports remaining"));
            return (
              <div className="dash-plan" data-testid="dash-plan-banner">
                <div className="dash-plan-info">
                  <span className="dash-plan-label">Plan</span>
                  <span className="dash-plan-name" data-testid="dash-plan-name">{planName}</span>
                  <span className="dash-plan-meta" data-testid="dash-plan-meta">{meta}</span>
                </div>
                {isFree && (!onboardingDone
                  ? <button className="dash-plan-cta" onClick={() => navigate("/welcome")} data-testid="dash-resume-btn">Resume setup</button>
                  : <button className="dash-plan-cta" onClick={() => navigate("/account")} data-testid="dash-upgrade-btn">Upgrade</button>
                )}
              </div>
            );
          })()}

          <TodayView today={today} navigate={navigate} />
        </div>
      </main>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * TodayView — Rule 0 · Answer Before Ask.
 *
 * Four sections, task-first order (owner-approved 2026-02-11):
 *   1. What Should I Prepare Today   (dominant CTA)
 *   2. Today's Ministry Snapshot
 *   3. What Needs Attention
 *   4. Recently Completed
 *
 * No fabricated data. Fields with no real value are OMITTED — no
 * "TBD", no "Not set", no placeholder rows.
 * ============================================================= */
function TodayView({ today, navigate }) {
  if (!today) {
    return <div className="td-loading" data-testid="td-loading">Loading your parish today…</div>;
  }

  // Rule-0 handoff — send parish to profile if the calendar cannot resolve.
  if (today.profile_incomplete) {
    return (
      <div className="td-profile-gap" data-testid="td-profile-incomplete">
        <h2 className="td-h">Complete your Parish Profile</h2>
        <p className="td-body">
          SundayBuilder needs your denomination and lectionary before it can begin planning worship for your parish.
        </p>
        <button
          className="td-primary"
          onClick={() => navigate("/parish-profile")}
          data-testid="td-profile-cta"
        >
          Open Parish Profile →
        </button>
      </div>
    );
  }

  const prepare = today.prepare_today || [];
  const snapshot = today.snapshot || null;
  const attention = today.attention || null;
  const recently = today.recently_completed || [];

  const openPlanner = (sunday_iso, draft_job_id) => {
    const qs = new URLSearchParams();
    if (sunday_iso) qs.set("date", sunday_iso);
    if (draft_job_id) qs.set("job_id", draft_job_id);
    navigate(qs.toString() ? `/worship-planner?${qs.toString()}` : "/worship-planner");
  };

  return (
    <div className="td" data-testid="today-view">
      {/* ---- 1 · What Should I Prepare Today (DOMINANT) -------- */}
      <section className="td-section td-section-hero" data-testid="td-prepare">
        <div className="td-section-eyebrow">Priority today</div>
        <h2 className="td-h td-h-hero">What should I prepare today</h2>
        {prepare.length === 0 ? (
          <p className="td-body">Nothing on the calendar needs preparation right now.</p>
        ) : (
          <ul className="td-list td-prepare-list">
            {prepare.map((p, i) => (
              <li key={p.sunday_iso} className={`td-row td-prepare-row${i === 0 ? " td-row-primary" : ""}`} data-testid={`td-prepare-${p.sunday_iso}`}>
                <div className="td-row-body">
                  <div className="td-row-title">{p.occasion || p.sunday_label}</div>
                  <div className="td-row-meta">
                    <span>{p.sunday_label}</span>
                    {p.liturgical_color && (
                      <>
                        <span className="td-dot" aria-hidden="true">·</span>
                        <span className={`td-color td-color-${p.liturgical_color}`}>
                          <span className="td-color-swatch" aria-hidden="true" />
                          {colorLabel(p.liturgical_color)}
                        </span>
                      </>
                    )}
                    <span className="td-dot" aria-hidden="true">·</span>
                    <span className="td-days">{p.days_away === 0 ? "today" : (p.days_away === 1 ? "tomorrow" : `${p.days_away} days`)}</span>
                    {p.status === "draft" && (
                      <>
                        <span className="td-dot" aria-hidden="true">·</span>
                        <span className="td-status-draft" data-testid={`td-draft-badge-${p.sunday_iso}`}>Draft in progress</span>
                      </>
                    )}
                  </div>
                </div>
                {i === 0 ? (
                  <button
                    className="td-primary td-primary-hero"
                    onClick={() => openPlanner(p.sunday_iso, p.draft_job_id)}
                    data-testid={`td-prepare-cta-${p.sunday_iso}`}
                  >
                    {p.status === "draft" ? "Continue draft →" : "Prepare dossier →"}
                  </button>
                ) : (
                  <button
                    className="td-ghost"
                    onClick={() => openPlanner(p.sunday_iso, p.draft_job_id)}
                    data-testid={`td-prepare-cta-${p.sunday_iso}`}
                  >
                    Prepare
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {snapshot && snapshot.recommendation && (
          <div className="td-recommend" data-testid="td-recommendation">
            <span className="td-recommend-label">Recommendation</span>
            <p className="td-recommend-body">{snapshot.recommendation}</p>
          </div>
        )}
      </section>

      {/* ---- 2 · Today's Ministry Snapshot (SECONDARY) --------- */}
      {snapshot && (
        <section className="td-section td-section-secondary" data-testid="td-snapshot">
          <h3 className="td-h td-h-secondary">Today's ministry snapshot</h3>
          <dl className="td-snapshot">
            {snapshot.next_celebration && (
              <div className="td-snap-row" data-testid="td-snap-next">
                <dt>Next celebration</dt>
                <dd>
                  {snapshot.next_celebration.occasion || snapshot.next_celebration.label}
                  {snapshot.next_celebration.label && snapshot.next_celebration.occasion && (
                    <span className="td-snap-meta"> · {snapshot.next_celebration.label}</span>
                  )}
                  {snapshot.next_celebration.liturgical_color && (
                    <span className={`td-color td-color-${snapshot.next_celebration.liturgical_color} td-snap-color`}>
                      <span className="td-color-swatch" aria-hidden="true" />
                      {colorLabel(snapshot.next_celebration.liturgical_color)}
                    </span>
                  )}
                </dd>
              </div>
            )}
            {snapshot.preparation && (
              <div className="td-snap-row" data-testid="td-snap-prep">
                <dt>Preparation</dt>
                <dd>
                  {prepStatusLabel(snapshot.preparation.status)}
                  {snapshot.preparation.estimated_minutes && (
                    <span className="td-snap-meta"> · about {snapshot.preparation.estimated_minutes} min</span>
                  )}
                  {snapshot.preparation.last_prepared_at && (
                    <span className="td-snap-meta"> · last dossier {relativeDate(snapshot.preparation.last_prepared_at)}</span>
                  )}
                </dd>
              </div>
            )}
            {snapshot.preacher && (
              <div className="td-snap-row" data-testid="td-snap-preacher">
                <dt>Clergy</dt>
                <dd>{snapshot.preacher} preaching</dd>
              </div>
            )}
            {snapshot.next_choir_rehearsal ? (
              <div className="td-snap-row" data-testid="td-snap-choir">
                <dt>Music</dt>
                <dd>
                  Choir rehearsal {snapshot.next_choir_rehearsal.day_name} · {snapshot.next_choir_rehearsal.time}
                  <span className="td-snap-meta"> · in {snapshot.next_choir_rehearsal.days_away} day{snapshot.next_choir_rehearsal.days_away === 1 ? "" : "s"}</span>
                </dd>
              </div>
            ) : (snapshot.choir_rehearsal && (
              <div className="td-snap-row" data-testid="td-snap-choir">
                <dt>Music</dt>
                <dd>Choir rehearsal {snapshot.choir_rehearsal}</dd>
              </div>
            ))}
            {snapshot.upcoming && snapshot.upcoming.length > 0 && (
              <div className="td-snap-row" data-testid="td-snap-upcoming">
                <dt>Upcoming</dt>
                <dd>
                  {snapshot.upcoming.map((u, i) => (
                    <span key={i} className="td-upcoming-item">
                      {u.occasion || u.label}
                      {u.days_away != null && <span className="td-snap-meta"> in {u.days_away} days</span>}
                      {i < snapshot.upcoming.length - 1 && <span className="td-sep"> · </span>}
                    </span>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </section>
      )}

      {/* ---- 3 · What Needs Attention (ALERT) ------------------ */}
      {attention && (attention.drafts_count > 0 || attention.profile_completeness_pct != null) && (
        <section className="td-section td-section-alert" data-testid="td-attention">
          <div className="td-alert-eyebrow" aria-hidden="true">
            <span className="td-alert-dot" />
            <span className="td-alert-label">Attention</span>
          </div>
          <h3 className="td-h td-h-secondary">What needs attention</h3>
          <ul className="td-list td-alert-list">
            {attention.drafts_count > 0 && (
              <li className="td-row" data-testid="td-attn-drafts">
                <div className="td-row-body">
                  <div className="td-row-title td-row-title-alert">{attention.drafts_count} draft{attention.drafts_count === 1 ? "" : "s"} in progress</div>
                  <div className="td-row-meta">Pick up where you left off before starting a new dossier.</div>
                </div>
                <button className="td-ghost" onClick={() => navigate("/reports?status=in_progress")} data-testid="td-attn-drafts-cta">Open drafts</button>
              </li>
            )}
            {attention.profile_completeness_pct != null && attention.profile_completeness_pct < 100 && (
              <li className="td-row" data-testid="td-attn-profile">
                <div className="td-row-body">
                  <div className="td-row-title td-row-title-alert">Parish Profile is {attention.profile_completeness_pct}% complete</div>
                  <div className="td-row-meta">The more the profile knows, the less SundayBuilder needs to ask.</div>
                </div>
                <button className="td-ghost" onClick={() => navigate("/parish-profile")} data-testid="td-attn-profile-cta">Complete profile</button>
              </li>
            )}
          </ul>
        </section>
      )}

      {/* ---- 4 · Recently Completed (ARCHIVAL) ----------------- */}
      {recently.length > 0 && (
        <section className="td-section td-section-archive" data-testid="td-recent">
          <div className="td-archive-eyebrow">Archive</div>
          <h4 className="td-h td-h-archive">Recently completed</h4>
          <ul className="td-list td-recent-list">
            {recently.map((r) => (
              <li key={r.report_id} className="td-row td-recent-row" data-testid={`td-recent-${r.report_id}`}>
                <div className="td-row-body">
                  <div className="td-row-title td-row-title-archive">
                    {r.occasion || r.sunday_label || "Dossier"}
                  </div>
                  <div className="td-row-meta td-row-meta-archive">
                    {r.sunday_label && <span>{r.sunday_label}</span>}
                    {r.completed_at && (
                      <>
                        {r.sunday_label && <span className="td-dot" aria-hidden="true">·</span>}
                        <span>Completed {relativeDate(r.completed_at)}</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  className="td-link-archive"
                  onClick={() => navigate(`/reports/${r.report_id}`)}
                  data-testid={`td-recent-open-${r.report_id}`}
                >Open →</button>
              </li>
            ))}
            <li className="td-row-all">
              <button className="td-link" onClick={() => navigate("/reports")} data-testid="td-recent-all">
                View all dossiers →
              </button>
            </li>
          </ul>
        </section>
      )}
    </div>
  );
}

/* ---- Small pure helpers ---- */
function colorLabel(key) {
  if (!key) return "";
  const map = {
    green: "Green", violet: "Violet", purple: "Violet",
    rose: "Rose", red: "Red", white: "White",
    white_gold: "White & Gold", "white-gold": "White & Gold",
    gold: "Gold", black: "Black",
  };
  return map[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
}
function prepStatusLabel(status) {
  if (status === "draft") return "Draft in progress";
  if (status === "ready") return "Dossier complete";
  return "Not yet started";
}
function relativeDate(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 1) return "today";
    if (diffDays === 1) return "yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}
